import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';
import { trackObjectChangeEvent } from '@/analytics/snowplow/analytics';
import { configTypeContextMap } from '@/analytics/snowplow/types';
import { userConfigsAtom } from '@/atoms/queries/userConfigs';
import {
  isGenericRenameModalOpenAtom,
  resetUserConfigActionStateAtom,
  userConfigIdAtom,
  userConfigNameAtom,
  userConfigTypeAtom,
} from '@/atoms/userConfigs';
import { useRenameConfig } from '@/hooks/useRenameConfig';
import { UserConfigType } from '@/types/userConfigs';
import { GenericSaveModal } from '../GenericSaveModal/GenericSaveModal';

const GenericRenameModalWithData = () => {
  const { t } = useTranslation(['general', 'modal']);
  const [userConfigName, setUserConfigName] = useAtom(userConfigNameAtom);

  const configs = useAtomValue(userConfigsAtom);

  const userConfigIdToAmend = useAtomValue(userConfigIdAtom);
  const userConfigType = useAtomValue(userConfigTypeAtom);
  const configToRenameData = configs.data.find(({ configId }) => configId === userConfigIdToAmend);

  const renameConfig = useRenameConfig();
  const resetUserConfigActionState = useSetAtom(resetUserConfigActionStateAtom);

  const otherConfigNames = configs.data
    .filter(({ configId, type }) => configId !== userConfigIdToAmend && type === configToRenameData?.type)
    .map(({ definition }) => (definition as any).titleSettings.title);

  const isPlayerSection = !!useMatch('/:locale/player/*');
  const entity = isPlayerSection ? 'Player' : 'Team';
  const trackingContext =
    userConfigType === UserConfigType.RADAR_TEMPLATE
      ? `${entity} - ${configTypeContextMap[userConfigType]}`
      : configTypeContextMap[userConfigType!];

  const handleConfirm = async () => {
    const { isSuccess } = await renameConfig();
    if (isSuccess) {
      resetUserConfigActionState();

      trackObjectChangeEvent({
        objectType: 'RuleSet',
        changeType: 'Rename',
        context: trackingContext,
      });
    }
  };

  const modalTitle = t(`renameModalTitle.${userConfigType}`, { ns: 'modal' });

  return (
    <GenericSaveModal
      id="rename-user-config-title"
      title={modalTitle}
      onConfirm={handleConfirm}
      onCancel={resetUserConfigActionState}
      confirmLabel={t('save')}
      value={userConfigName ?? ''}
      onChange={setUserConfigName}
      existingConfigNames={otherConfigNames}
    />
  );
};

export const GenericRenameModal = () => {
  const isRenameModalOpen = useAtomValue(isGenericRenameModalOpenAtom);

  return (
    isRenameModalOpen && (
      <Suspense>
        <GenericRenameModalWithData />
      </Suspense>
    )
  );
};
